import { c as createFocusTrap } from './focus-trap.esm-CwGG3vRJ.js';
import { onClickOutside } from './utils-on-click-outside.js';

class ContentSelect extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-select-button]');
    this.initialButtonContent = this.button.innerHTML;

    this.disabled = this.hasAttribute('disabled') || this.hasAttribute('data-disabled');
    if (this.disabled) {
      this.disable();
    }

    // Handle any click on the element
    this.addEventListener('click', (event) => {
      if (this.disabled) return;

      if (event.target.closest('[data-select-button]')) {
        this.button = event.target.closest('[data-select-button]');
        this.toggle();
      }
      if (event.target.closest('[data-value]')) {
        this.selectOption(event.target.closest('[data-value]'));
      }
    });

    // Listen for key:escape events
    this.unsubscribeEscapeKey = subscribe('key:escape', () => this.close());

    // Handle click outside
    onClickOutside(this, () => this.close());

    // Create the focus trap
    this.trap = createFocusTrap(this, {
      // Allow clicks outside the dialog to close the dialog
      allowOutsideClick: true,
    });

    // Listen for refresh events
    this.addEventListener('content-select:refresh', this.refresh.bind(this));
  }

  disconnectedCallback() {
    this.unsubscribeEscapeKey();
  }

  /**
   * Refresh the select list by reinitializing it
   */
  refresh() {
    // Reset the list so it will be reinitialized
    this.list = null;
    this.initList();
  }

  /**
   * Initialize the list and all related elements
   */
  initList() {
    if (!this.list) {
      this.list = this.querySelector('[data-select-list]');
      if (!this.list) return; // Ensure list exists before proceeding
      this.options = this.list.querySelectorAll('[role="menuitem"]');
      this.fallbackSelect = this.querySelector('select');
      this.searchInput = this.list.querySelector('[data-select-search]');
      this.noResults = this.list.querySelector('[data-no-results]');
      this.initSelectedOption();
      if (this.searchInput)
        this.searchInput.addEventListener('input', this.filterOptions.bind(this));
    }
  }

  /**
   * Set the selected option to the value of the fallback select
   */
  initSelectedOption() {
    if (!this.fallbackSelect) return;

    const selectedValue = this.fallbackSelect.value;
    const selectedOption = Array.from(this.options).find(
      (option) => option.dataset.value === selectedValue
    );

    if (!selectedOption) return;
    selectedOption.setAttribute('data-selected', '');
  }

  /**
   * Toggle the select list
   */
  toggle() {
    if (this.disabled) return;
    this.initList();
    if (!this.list) return;
    this.list.ariaHidden === 'true' ? this.open() : this.close();
  }

  /**
   * Open the select list
   */
  open() {
    if (this.disabled) return;
    this.initList();
    if (!this.list || this.list.ariaHidden === 'false') return;
    this.list.ariaHidden = 'false';
    this.button.ariaExpanded = 'true';
    this.trap.activate();
    if (this.searchInput) {
      this.searchInput.value = '';
      this.searchInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  /**
   * Close the select list and reset the search input
   */
  close() {
    this.initList();
    if (this.list.ariaHidden === 'true') return;
    this.trap.deactivate();
    setTimeout(() => {
      this.list.ariaHidden = 'true';
      this.button.ariaExpanded = 'false';
    }, 100);
  }

  /**
   * Select an option
   */
  selectOption(optionElement) {
    this.initList();

    if (this.disabled) return;

    if (this.options) {
      this.options.forEach((option) => option.removeAttribute('data-selected'));
    }

    if (!optionElement) {
      if (this.button && this.initialButtonContent !== undefined) {
        this.button.innerHTML = this.initialButtonContent;
      }
      if (this.fallbackSelect) {
        this.fallbackSelect.value = '';
      }
      this.close();
      return;
    }

    if (!this.fallbackSelect) return;

    optionElement.setAttribute('data-selected', '');
    this.button.innerHTML = '';
    Array.from(optionElement.cloneNode(true).children).forEach((child) => {
      this.button.appendChild(child);
    });

    this.updateFallbackSelectOption(optionElement);
    this.close();
  }

  /**
   * Update the fallback select option
   */
  updateFallbackSelectOption(optionElement) {
    this.fallbackSelect.value = optionElement.dataset.value;
    this.fallbackSelect.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Filter the options
   */
  filterOptions(event) {
    const searchTerm = event.target.value.toLowerCase().trim();

    if (!searchTerm) {
      this.showAllOptions();
      return;
    }

    const hasVisibleOptions = this.filterOptionsByTerm(searchTerm);
    this.toggleNoResultsMessage(!hasVisibleOptions);
  }

  /**
   * Filter the options by term
   */
  filterOptionsByTerm(searchTerm) {
    let hasVisibleOptions = false;

    this.options.forEach((option) => {
      if (option.hasAttribute('data-filtered-hidden')) {
        return;
      }

      const text = option.textContent.toLowerCase();
      const isVisible = text.includes(searchTerm);
      option.classList.toggle('hidden', !isVisible);
      if (isVisible) hasVisibleOptions = true;
    });

    return hasVisibleOptions;
  }

  /**
   * Toggle the no results message
   */
  toggleNoResultsMessage(show) {
    if (!this.noResults) return;
    this.noResults.classList.toggle('hidden', !show);
  }

  /**
   * Show all options
   */
  showAllOptions() {
    this.options.forEach((option) => {
      if (!option.hasAttribute('data-filtered-hidden')) {
        option.classList.remove('hidden');
      }
    });
    this.toggleNoResultsMessage(false);
  }

  /**
   * Disable the select
   */
  disable() {
    this.disabled = true;
    this.setAttribute('data-disabled', 'true');
    if (this.button) {
      this.button.disabled = true;
    }
    this.classList.add('is-disabled');
    this.close();
  }

  /**
   * Enable the select
   */
  enable() {
    this.disabled = false;
    this.removeAttribute('data-disabled');
    if (this.button) {
      this.button.disabled = false;
    }
    this.classList.remove('is-disabled');
  }
}

customElements.define('content-select', ContentSelect);
